import * as React from "react";
import {Types}    from "@Common/types";

import {cst, cstAssign}    from "@Common/react/chain-services";
import {Static2}           from "@Common/react/static-2";
import {UseState}          from "@Common/react/use-state";
import {ConfirmDialog}     from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {ModalService3}     from "@Common/ui-components/modal-service-3/modal-service-3";
import {needsConfirmation} from "@Common/ui-components/needs-confirmation/needs-confirmation";

import {ShippingAddressForm} from "@Xpresso/routes/common/shipping-address-form/shipping-address-form";
import {ModalLayout}         from "@Xpresso/routes/common/modal-layout/modal-layout";

interface VarStack {
    form                    : Types.Xpresso.Common.Shipping.AddressForm.FormNext,
    isBusyState             : Types.Common.React.UseState.UseStateNext<boolean>,
    hasUnsavedChangesStatic : Types.Common.React.Static2.Static2Next<boolean>,
    modal                   : Types.Common.UiComponents.ModalService3.ModalService3RootNext<void>,
    exitConfirmationModal   : Types.Common.UiComponents.ModalService3.ModalService3RootNext<boolean, boolean>,
    safeAction              : (action: () => void) => void,
    safeLinkClickHandler    : () => Promise<boolean>,
}

export const Component = ({
    onSubmit: handleSubmit,
    next,
    value
}:Types.Xpresso.Account.Company.AddressBookRoute.ComponentPropsShippingAddressFormModal) => cst<VarStack>(
    cstAssign("exitConfirmationModal", ({}, next) => ConfirmDialog({next})),
    
    cstAssign("hasUnsavedChangesStatic", ({}, next) => Static2({next, getInitValue: () => false})),

    cstAssign("safeAction", ({
        exitConfirmationModal,
        hasUnsavedChangesStatic: {get: getHasUnsavedChanges, set: setHasUnsavedChanges},
    }, next) => (
        next(async (action) => {
            const confirmed = !getHasUnsavedChanges() || await needsConfirmation({
                confirmDialog: exitConfirmationModal
            });
            if (confirmed) {
                action();
                setHasUnsavedChanges(false);
                return true;
            }
            return false;
        })
    )),
    cstAssign("form", ({hasUnsavedChangesStatic}, next) => ShippingAddressForm({next, initValue: value, hasUnsavedChangesStatic})),
    
    cstAssign("isBusyState", ({}, next) => UseState({next, initValue: false})),

    cstAssign("modal", ({form, isBusyState, safeAction, hasUnsavedChangesStatic}, next) => ModalService3({
        xdQaName: "Form Modal",
        rootNext: next,

        contentService : ({close, next}) => next({getContent: () => ModalContent({
            close,
            canSave           : form.form.validate() && hasUnsavedChangesStatic.get(),
            isBusy            : isBusyState.value,
            onSaveButtonClick : async () => {
                isBusyState.onChange(true);
                const success = await handleSubmit?.(form.form.value);
                isBusyState.onChange(false);

                if (success) {
                    hasUnsavedChangesStatic.set(false)
                    close()
                };
            },
            renderForm        : () => form.render({showErrors: true}),
        })}),
        onClose        : async () => safeAction(() => {}),
        onPassiveClose : async () => !isBusyState.value, // only allow closing after save is complete
        urlParam: "shipping",
    })),

    ({form, modal}) => next({
        show: () => {
            form.form.fromValue(value);
            modal.show();
        }
    }),
);

interface ModalContentProps {
    canSave           : boolean,
    close             : () => void,
    isBusy            : boolean,
    onSaveButtonClick : React.MouseEventHandler,
    renderForm        : () => JSX.Element,
}

const ModalContent = ({canSave, close, isBusy, onSaveButtonClick, renderForm}:ModalContentProps) => ModalLayout({
    title: <div>Update Company Shipping Address</div>,
    content: (
        <div style={{padding: "1em"}}>{renderForm()}</div>
    ),
    buttons: [
        {
            label: "Cancel",
            disabled: isBusy,
            onClick: close,
        },
        {
            label: "Save",
            disabled: isBusy || !canSave,
            onClick: onSaveButtonClick,
            primary: true,
        },
    ]
});
